<template>
  <!-- prettier-ignore -->
  <router-link
    v-tooltip="info"
    :data-group-id="group.id"
    :to="to">
    <div :class="classes">
      <div
        v-if="group.recommended"
        class="teaser-recommendation"
        v-text="$t(content).recommendation"
      />

      <div class="teaser-content">
        <div
          v-if="hasSelectedProduct"
          class="teaser-selected">
          <ovp-icon
            art="light"
            name="check"
          />
        </div>

        <div
          class="teaser__title"
          v-text="$t(content).title"
        />

        <div class="teaser__price">
          <ovp-price
            :prefix="(group.products.length > 1) ? $t('product.asof') : null"
            :price="group.price"
            :suffix="$t('product.currency')"
          />
          <span
            v-if="reductions"
            class="voc"
            v-text="'*'"
          />
        </div>

        <div
          class="teaser__description"
          v-text="$t(content).description"
        />

        <ovp-button
          v-if="$browser.isDesktop"
          secondary
          v-text="$t('product.toOffer')"
        />
        <a
          v-else
          class="teaser__action"
          v-text="$t('product.toOffer')"
        />
      </div>
    </div>
  </router-link>
</template>

<script>
import { mapGetters } from 'vuex'
import OvpPrice from '@/components/Product/Price'

export default {
  name: 'Group',

  components: {
    OvpPrice,
  },

  props: {
    categoryId: {
      type: String,
      required: true,
    },

    collapsed: {
      type: Boolean,
      default: false,
    },

    group: {
      type: Object,
      required: true,
    },

    personId: {
      type: String,
      required: true,
    },

    selected: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters('basket', ['getProductsFromGroup']),

    classes() {
      return {
        teaser: true,
        'teaser--selected': this.selected,
        'teaser--collapsed': this.collapsed,
      }
    },

    content() {
      return `content.groups.${this.group.id}`
    },

    hasSelectedProduct() {
      return !!this.getProductsFromGroup(this.personId, this.group.id).filter(p => p.selected).length
    },

    info() {
      return (
        this.group.messages
          .map(m => {
            return this.$t(`error.${m.key}`)
          })
          .join('; ')
          .trim() || false
      )
    },

    reductions() {
      return this.group.reductions
        ?.reduce((reductions, reduction) => {
          reductions.push(`${this.$t(`info.${reduction.type}`)} ${reduction.amount} ${this.$t('product.currency')}`)
          return reductions
        }, [])
        .join('<br>')
    },

    to() {
      if (this.collapsed && this.selected) {
        return `/products/${this.personId}/${this.categoryId}`
      } else {
        return `/products/${this.personId}/${this.categoryId}/${this.group.id}`
      }
    },
  },
}
</script>

<style name="mobile" scoped>
.teaser {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  justify-content: flex-end;
  height: 100%;
  color: var(--c-black);
  font-weight: normal;

  &-recommendation {
    font-weight: bold;
    background-color: var(--c-sanitas-green);
    color: var(--c-white);
    font-size: 15px;
    line-height: 20px;
    padding: 5px 15px;
  }

  &-content {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 0 1 auto;
    padding: 20px 15px;
    border: 1px solid var(--c-black);
    transition: all 0.3s ease-in-out;
  }

  &__action {
    text-align: right;
  }

  &__title {
    font-size: 15px;
    line-height: 20px;
    font-weight: bold;
  }

  &__price {
    font-size: 15px;
    white-space: nowrap;

    .price {
      margin: 0 5px 0 0;
    }

    >>> .price__value,
    /deep/ .price__value {
      font-size: 18px;
      line-height: 22px;
    }
  }

  &__reduction {
    color: var(--c-black);
  }

  &__description {
    flex: 1 1 auto;
    margin: 15px 0 20px 0;
    font-size: 15px;
    line-height: 20px;
  }

  &--collapsed {
    position: relative;

    &:after {
      content: '';
      position: absolute;
      left: calc(50% - 7.5px);
      bottom: 5px;
      height: 15px;
      width: 15px;
      background: transparent;
      transform: rotate(135deg);
      z-index: 0;

      transition: all 0.35s ease-in-out;
    }

    .teaser {
      &-content {
        height: auto !important;
        padding: 15px 15px 15px 20px;
        z-index: 1;
        background: var(--c-white);
      }

      &__title {
        white-space: nowrap;
      }

      &__price {
        font-size: 15px;
        line-height: 20px;
      }

      &__description,
      &__action {
        display: none;
      }
    }

    .teaser-selected {
      background: linear-gradient(225deg, rgba(91, 172, 38, 1) 0%, rgba(91, 172, 38, 1) 50%, rgba(0, 212, 255, 0) 50%);

      .icon {
        color: var(--c-white);
      }
    }

    &.teaser--selected {
      position: relative;

      &:after {
        content: '';
        position: absolute;
        left: calc(50% - 7.5px);
        bottom: -7px;
        height: 15px;
        width: 15px;
        background: linear-gradient(
          225deg,
          rgba(91, 172, 38, 1) 0%,
          rgba(91, 172, 38, 1) 50%,
          rgba(0, 212, 255, 0) 50%
        );
      }

      .teaser {
        &-content {
          background-color: var(--c-original-green);
          border-color: var(--c-original-green);
          color: var(--c-white);
        }

        &-selected {
          background: linear-gradient(
            225deg,
            rgba(255, 255, 255, 1) 0%,
            rgba(255, 255, 255, 1) 50%,
            rgba(0, 212, 255, 0) 50%
          );

          .icon {
            color: var(--c-original-green);
          }
        }

        &__title {
          font-size: 15px;
          line-height: 20px;
          color: inherit;
          font-weight: bold;
        }

        &__price {
          font-size: 15px;
          line-height: 20px;
          color: inherit;
        }

        &__toggle {
          position: absolute;
          right: 2px;
          top: 12px;
        }
      }
    }
  }

  &-selected {
    position: absolute;
    top: 1px;
    right: 1px;

    height: 35px;
    width: 35px;
    text-align: center;

    background: var(--c-white)
      linear-gradient(225deg, rgba(91, 172, 38, 1) 0%, rgba(91, 172, 38, 1) 50%, rgba(0, 212, 255, 0) 50%);
    padding-left: 15px;

    .icon {
      color: var(--c-white);
    }
  }
}

a {
  text-decoration: none;

  &:hover {
    .teaser--collapsed:not(.teaser--selected) {
      .teaser-content {
        color: var(--c-original-green);
      }
    }

    .teaser:not(.teaser--collapsed) {
      .teaser-content {
        color: var(--c-original-green);
      }
    }
  }
}

.voc {
  font-size: 15px;
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .teaser {
    &__description {
      margin-bottom: 50px;
    }

    button {
      width: 100%;
    }

    &--collapsed {
      button {
        display: none;
      }
    }
  }
}
</style>
